<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <div class="space-y-4">
      <Input
        v-model="formData.email"
        type="email"
        label="Email address"
        placeholder="Enter your email"
        required
        :error="errors.email"
        :disabled="isLoading"
      />

      <Input
        v-model="formData.password"
        type="password"
        label="Password"
        placeholder="Enter your password"
        required
        :error="errors.password"
        :disabled="isLoading"
      />
    </div>

    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <input
          id="remember-me"
          v-model="formData.rememberMe"
          name="remember-me"
          type="checkbox"
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label for="remember-me" class="ml-2 block text-sm text-gray-900"> Remember me </label>
      </div>

      <div class="text-sm">
        <button
          type="button"
          @click="$emit('forgot-password')"
          class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
        >
          Forgot your password?
        </button>
      </div>
    </div>

    <div>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        :loading="isLoading"
        :disabled="!isFormValid"
        full-width
      >
        Sign in
      </Button>
    </div>

    <div class="text-center">
      <p class="text-sm text-gray-600">
        Don't have an account?
        <button
          type="button"
          @click="$emit('register')"
          class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
        >
          Sign up
        </button>
      </p>
    </div>
  </form>
</template>

<script setup lang="ts">
  import { computed, reactive } from 'vue';
  import { Button, Input } from '@asabdev/asab-ui';
  import { useAuth } from '../composables/useAuth';
  import { AUTH_ERROR_CODES, isAuthError } from '@/lib/api/errors';
  import type { LoginFormData } from '@/types/auth';
  import type { AxiosError } from 'axios';

  interface Props {
    redirectTo?: string;
  }

  // Props are defined but not used in this component
  defineProps<Props>();

  const emit = defineEmits<{
    success: [user: unknown];
    error: [error: string];
    'forgot-password': [];
    register: [];
    'verification-needed': [email: string];
    // Account is disabled in Keycloak. `status` discriminates the reason
    // (pending_approval | banned | suspended | deactivated | disabled).
    // Page renders a per-status banner; waitlist_url is only populated
    // for pending_approval.
    'account-disabled': [
      info: {
        status: 'pending_approval' | 'banned' | 'suspended' | 'deactivated' | 'disabled';
        message: string;
        waitlist_url: string;
        support_email: string;
      },
    ];
  }>();

  const { login, isLoading, error: authError, clearError, logout } = useAuth();

  // Form data
  const formData = reactive<LoginFormData & { rememberMe: boolean }>({
    email: '',
    password: '',
    rememberMe: false,
  });

  // Form validation errors
  const errors = reactive({
    email: '',
    password: '',
  });

  // Validation rules
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (email: string): string => {
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (password.length < 3) return 'Password must be at least 3 characters';
    return '';
  };

  // Computed properties
  const isFormValid = computed(() => {
    return formData.email && formData.password && !errors.email && !errors.password;
  });

  // Methods
  const validateForm = (): boolean => {
    errors.email = validateEmail(formData.email);
    errors.password = validatePassword(formData.password);

    return !errors.email && !errors.password;
  };

  const handleSubmit = async () => {
    // Clear previous auth errors
    clearError();

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      // First, log out any existing session to prevent conflicts with existing tokens
      // This helps fix the issue where the app crashes when an unverified user tries to log in
      if (authError.value?.includes('verify') || authError.value?.includes('verification')) {
        await logout();
      }

      const credentials: LoginFormData = {
        email: formData.email,
        password: formData.password,
      };

      const response = await login(credentials);

      // If we get here, login was successful and user is verified
      // Emit success with the response
      emit('success', response);
    } catch (error) {
      // Account disabled in Keycloak. Backend reads UserProfile.AccountStatus
      // to pick a per-reason error code; map each to a status discriminator
      // so the page can render the right banner (waitlist for pending,
      // support contact for banned, billing link for suspended).
      const axErr = error as AxiosError<{
        error?: string;
        waitlist_url?: string;
        support_email?: string;
      }>;
      const disabledStatus = isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_PENDING_APPROVAL)
        ? 'pending_approval'
        : isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_ACCOUNT_BANNED)
          ? 'banned'
          : isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_ACCOUNT_SUSPENDED)
            ? 'suspended'
            : isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_ACCOUNT_DEACTIVATED)
              ? 'deactivated'
              : isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_ACCOUNT_DISABLED)
                ? 'disabled'
                : null;
      if (disabledStatus) {
        const data = axErr.response?.data ?? {};
        emit('account-disabled', {
          status: disabledStatus,
          message: data.error ?? 'Your account is currently disabled.',
          waitlist_url: data.waitlist_url ?? '',
          support_email: data.support_email ?? '',
        });
        return;
      }
      // Handle special case for unverified users
      // Check for the LOGIN_USER_NOT_VERIFIED error code or verification-related messages
      if (
        authError.value === AUTH_ERROR_CODES.LOGIN_USER_NOT_VERIFIED ||
        authError.value === 'Please verify your email address before logging in' ||
        authError.value?.includes('verify') ||
        authError.value?.includes('verification') ||
        isAuthError(error as AxiosError, AUTH_ERROR_CODES.LOGIN_USER_NOT_VERIFIED)
      ) {
        console.log('Unverified user detected, emitting verification-needed event');
        // Clear any existing auth state to prevent conflicts
        await logout();

        // Emit verification needed event with email
        emit('verification-needed', formData.email);

        // Also emit the error for display
        emit('error', authError.value || 'Please verify your email address before logging in');
      } else if (isAuthError(error as AxiosError, AUTH_ERROR_CODES.REGISTER_USER_ALREADY_EXISTS)) {
        // User already exists but trying to login - redirect to verification
        console.log('User already exists, redirecting to verification');
        await logout();
        emit('verification-needed', formData.email);
        emit('error', 'Please verify your email address to complete your account setup');
      } else {
        // Regular login error
        const errorMessage = authError.value || 'Login failed. Please try again.';
        emit('error', errorMessage);
      }
    }
  };
</script>
